/* eslint-disable react/destructuring-assignment */
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, connect, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import * as showToastAction from '@modules/toasts/features/toastsSlice';
import { useSaveCreabotsProgress } from '@services/rest/creabots/hooks';
import * as routes from '@educabot/educablocks-cosmos';
import { v1 as uuidv1 } from 'uuid';
import Header from '@sections/creabots/components/header';
import Bottom from '@sections/creabots/components/bottom';
import Blockly from '@modules/blocklyNew/containers/blockly';
import BlocklyBuild from '@modules/blockly/components/blocklyBuild';
import {
  build,
  setMLModel,
  setMLModelJson,
  setMLModelWeights,
  setMLTrainingParams,
  setMLTrainingStep,
  setMLSamples,
  setMLModelTrained,
  setMLSamplesModalActive,
  setMLActive,
} from '@modules/blockly/features/blocklySlice';
import { useCheckLogged } from '@utils/hooks/auth';
import { Kits } from '@sections/creabots/features/kits';
import { boards as Boards, getBoardById } from '@educabot/educablocks-boards';
import ConnectionModal from '@sections/creabots/components/connectionModal';
import BuildProgressModal from '@sections/creabots/components/buildProgressModal';
import { useBlockly } from '@modules/blockly/features/hooks';
import { useCreateUpdateProject, useGetUserProject } from '@modules/projects/features/hooks';
import boardTypes from '@modules/boards/features/constants';
import { BOARD_ID_TYPE } from '../types/kits';
import { getUserAgent } from '../../../helpers/browserAgent';
import Agent from '../../../helpers/agent';
import { store } from '../../../state/store';
import styles from './project.mod.scss';
import { selectCreabots, setSelectedKit } from '../features/creabotsSlice';
import MLPanel from '@modules/blockly/components/MLPanel';
import { TRAIN_STEPS, MODEL_TYPES } from '@utils/hooks/machineLearning/params';
import EmptyState from '../components/emptyState';


const CreabotsUserProject = (props) => {
  const intl = useIntl();
  const location = useLocation();
  const dispatch = useDispatch();
  const { kits } = useSelector(selectCreabots)
  const [id, setId] = useState(0);
  const [board, setBoard] = useState({});
  const [workspace, setWorkspace] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [kitName, setKitName] = useState(null);
  const [kitId, setKitId] = useState(1);
  const [tabInfo, setTabInfo] = useState({});
  const [socket, setSocket] = useState(null);
  const [userAgent, setUserAgent] = useState({});
  const [blocklyId, setBlocklyId] = useState(uuidv1());
  const [intervalPortListing, setIntervalPortListing] = useState(null);
  const userProject = useGetUserProject(projectId || 0);
  const [projectName, setProjectName] = useState('creabots.untitled');
  const userDataState = useCheckLogged(true, '/kits/ingresar');
  const blocklyState = useBlockly();
  const blocklyRef = useRef();
  const [iaModel, setIAModel] = useState(null);
  // console.log('=============userProject', userProject);
  const { createUpdateProject } = useCreateUpdateProject(userDataState?.data?.userId || 0, 3000);
  const mlHandler = useRef();
  const [showEmptyState, setShowEmptyState] = useState(false);
  const mlHydratedRef = useRef(false);

  const isValidProject = userProject.status === 'success' ? (!!userProject.selectedProject && !!userProject.selectedProject.id && !isNaN(userProject.selectedProject.id) && userProject.selectedProject.id > 0) : true;

  useEffect(() => {
    const boardId = Kits[kitName]?.boardId || BOARD_ID_TYPE.INITIATION;
    setBoard(getBoardById(Boards, boardId));
  }, [kitName]);

  useEffect(() => {
    if (tabInfo?.id) {
      startSocket(true);
    }
  }, [tabInfo?.id]);

  useEffect(() => {
    // Con un projectId real hay que esperar el GET antes de armar tabInfo: si no,
    // el autosave congela el nombre default y pisa el título real en el backend.
    const waitingForRealProject = !!projectId && userProject.status !== 'success';
    if (board?.id && !waitingForRealProject) {
      setTabInfo({
        tagsSelected: [],
        projectId: userProject.selectedProject?.id || 0,
        id: blocklyId,
        name: userProject.selectedProject?.title || intl.formatMessage({ id: projectName }),
        description: userProject.selectedProject?.description || '',
        userId: userProject.selectedProject?.userId || userDataState?.data?.userId || 0,
        board: userProject.selectedProject?.jsonBody?.board || board,
        port: userProject.selectedProject?.jsonBody?.port || '',
        agentPorts: [],
        boardName: userProject.selectedProject?.jsonBody?.board.name || board.name || '',
        className: userProject.selectedProject?.jsonBody?.board.class || board.class || '',
        portOpen: false,
        active: true,
        disabled: false,
        withToolTip: false,
        withWorkspace: userProject.selectedProject?.jsonBody?.withWorkspace || false,
        rightPanel: '',
        diagramImageUrl: userProject.selectedProject?.imageUrl || '',

        boardType: 'senior',
        code: userProject.selectedProject?.jsonBody?.code || '',
      });

      // El ML se hidrata una sola vez: el autosave re-dispara este efecto con un snapshot
      // viejo que pisaría las clases que MLPanel ya agregó.
      if (!mlHydratedRef.current) {
        const machineLearning = userProject.selectedProject?.jsonBody?.machineLearning || {};
        if (machineLearning.model) {
          setIAModel(machineLearning.model || null);
          dispatch(setMLModel(machineLearning.model || null));
          dispatch(setMLModelJson(machineLearning.modelJson || null));
          dispatch(setMLModelWeights(machineLearning.modelWeights || null));
          dispatch(setMLTrainingParams(machineLearning.trainingParams || {}));
          dispatch(setMLTrainingStep(machineLearning.trainingStep || TRAIN_STEPS.TEACH));
          dispatch(setMLSamples(machineLearning.samples || {}));
          dispatch(setMLModelTrained(machineLearning.modelTrained || false));
          dispatch(setMLSamplesModalActive(machineLearning.samplesModalActive || null));
          dispatch(setMLActive(machineLearning.active || false));
        }
        mlHydratedRef.current = true;
      }
    }
  }, [userProject.selectedProject?.jsonBody, board, projectId, userProject.status]);

  useEffect(() => {
    setProjectId(parseInt(location
      .pathname
      .split('/')
      .filter(Boolean)[2]
      .split('-')
      .filter(Boolean)[1], 10) || 0);

    setKitName(location
      .pathname
      .split('/')
      .filter(Boolean)[1] || null);

    return () => {
      setId(0);
      setBlocklyId(uuidv1());
      setSocket(null);
      setKitName(null);
      setProjectId(null);
    };
  }, []);

  useEffect(() => {
    if (socket && !intervalPortListing) {
      const intervalPortListing = setInterval(() => {
        socket.findPorts();
      }, 1000);
      setIntervalPortListing(intervalPortListing);
    }

    return () => {
      clearInterval(intervalPortListing);
      setIntervalPortListing(null);
    }
  }, [socket?.agentType]);

  useEffect(() => {
    if (blocklyRef.current?.withWorkspace !== blocklyState?.project?.withWorkspace) {

      const machineLearningToSave = {
        active: blocklyState?.machineLearning?.active || false,
        model: blocklyState?.machineLearning?.model || null,
        trainingStep: blocklyState?.machineLearning?.trainingStep || TRAIN_STEPS.TEACH,
        trainingParams: blocklyState?.machineLearning?.trainingParams || {},
        samples: (iaModel === MODEL_TYPES.SOUNDS && mlHandler.current) ? mlHandler.current.getSamples(true) : blocklyState?.machineLearning?.samples || {},
        modelTrained: blocklyState?.machineLearning?.modelTrained || false,
        modelJson: blocklyState?.machineLearning?.modelJson || null,
        modelWeights: blocklyState?.machineLearning?.modelWeights || null,
        samplesModalActive: blocklyState?.machineLearning?.samplesModalActive || false,
      };

      const projectToSave = {
        ...userProject.selectedProject,
        id: tabInfo.projectId || 0,
        title: tabInfo.name,
        description: tabInfo.description,
        boardType: tabInfo.boardType,
        blocksCss: blocklyState.project.blocksCss,
        canvasCss: blocklyState.project.canvasCss,
        isCreabots: true,
        jsonBody: {
          ...userProject.selectedProject?.jsonBody,
          ...blocklyState.project,
          board: tabInfo.board,
          machineLearning: machineLearningToSave,
        },
      };

      createUpdateProject(projectToSave);
    }

    blocklyRef.current = blocklyState?.project || {};
  }, [blocklyState?.project?.withWorkspace, blocklyState?.project?.code]);

  const startSocket = () => {
    const newSocket = (tabInfo.board.profile !== 'codit' && tabInfo.board.profile !== 'drone')
      ? new Agent(store, tabInfo.board)
      : null

    if (newSocket?.agentType) {
      getUserAgent().then((ua) => {
        setUserAgent(ua);
        newSocket.setPlatform(ua.platform, 4000);
        // Hack to force redraw in order to update socket.agentType property
        setTimeout(() => {
          setId(1);
        }, 4000);
      });
    }

    setSocket(newSocket);
  }

  const setAgent = (type = 'serial') => {
    clearInterval(intervalPortListing);
    setIntervalPortListing(null);
    socket.forceAgent(type);
  }

  const buildBlockly = (params) => {
    dispatch(build(params));
  }

  const changeBuildPort = (name) => {
    // setTabInfo({ ...tabInfo, port: name });
  }

  const handleGoHome = () => {
    if (location.pathname.includes('proyecto')) {
      dispatch(setSelectedKit({
        id: 0,
        name: 'Mis Proyectos',
        projects: [],
      }))
      window.location.href = `${routes.bloquesUri}/kits/mis-proyectos`
      return
    }
    // history.push(`/kits/creabots/${kits.selected.urlName}`)
    window.location.href = `${routes.bloquesUri}/kits/creabots/${kits.selected.urlName}`
  }

  const scrollWorkspaceByX = (ws, x = 0, y = 0) => {
    // `scroll()` de la v13 toma el offset de pantalla directo y ya clampea al
    // contenido: no agregar clamps a mano, que cancelan el desplazamiento.
    const metrics = ws.getMetrics();
    if (metrics) {
      ws.scroll(x + 20, y + 10);
    }
  }

  const updateWorkspace = (ws) => {
    if (ws) {
      if (!workspace) {
        scrollWorkspaceByX(ws, 10, 0);
      }
      setWorkspace(ws);
    }
  }

  const handleSetMLHandler = (currentMLHandler) => {
    mlHandler.current = currentMLHandler;
  }

  return (
    <div className={styles.project}>
      {isValidProject ? (
        <Header
          titleString={userProject.selectedProject?.title || intl.formatMessage({ id: projectName })}
          handleGoHome={handleGoHome}
        />
      ) : null}
      <div className={`${styles.courses} ${iaModel ? styles.iaPanel : ''}`}>
        {isValidProject && tabInfo.id ? (
          <>
            <Blockly
              tabInfo={tabInfo}
              socketAgent={socket}
              buildAllowed={false}
              portChangeAllowed={true}
              noCodePanel={true}
              setWorkspace={updateWorkspace}
              isCreabots={true}
              isFreeProject={true}
            />
            {socket ? (
              <Bottom
                buildingFunction={buildBlockly}
                board={tabInfo.board}
                socketAgent={socket}
              />
            ) : ('')}
            <BlocklyBuild
              board={tabInfo.board}
              socketAgent={socket}
              id={tabInfo?.id || ''}
              compilerBoard={tabInfo?.board?.compilerBoard || ''}
              userAgent={userAgent}
              userData={userDataState?.data || {}}
              setAgent={setAgent}
              changeBuildPort={changeBuildPort}
              mainModal={ConnectionModal}
              progressModal={BuildProgressModal}
              buildingFunction={buildBlockly}
            />
            {iaModel ? (
              <MLPanel
                isCreabots={true}
                isFreeProject={true}
                workspace={workspace}
                iaModel={iaModel || null}
                board={tabInfo.board || {}}
                setMLHandler={handleSetMLHandler}
              />
            ) : ('')}
          </>
        ) : (
          showEmptyState && (
            <div>
              <EmptyState />
            </div>
          )
        )}
      </div>
    </div>
  );
};


const mapDispatchToProps = (dispatch) => {
  return {
    showToast: (params) => dispatch(showToastAction.showToast(params)),
  }
}

export default connect(null, mapDispatchToProps)(CreabotsUserProject);
